import classNames from 'classnames'
import React from 'react'

import { type UserGroupName } from '../api/interfaces/sconnStudyData'
import { useSurvey } from '../module/survey/hook'

import FormContent from './surveyFormContentLayout'

const ICON_BASE = `${process.env.PUBLIC_URL}/img/learning-keyword`

type LearnerGroupOption = {
  id: UserGroupName
  label: string
  description?: string
  iconSrc: string
  /** 학년 선택이 필요한 그룹만 보유 (초등 1~6, 중·고 1~3) */
  grades?: number[]
}

const LEARNER_GROUP_OPTIONS: LearnerGroupOption[] = [
  {
    id: 'university',
    label: '대학생',
    iconSrc: `${ICON_BASE}/ic_colleage_student.svg`,
  },
  {
    id: 'repeat_applicant',
    label: 'N수생/반수생',
    iconSrc: `${ICON_BASE}/ic_retake_student.svg`,
  },
  {
    id: 'general',
    label: '일반인',
    description: '자격증, 공무원, 취업',
    iconSrc: `${ICON_BASE}/ic_general_adult.svg`,
  },
  {
    id: 'high_school',
    label: '고등학생',
    iconSrc: `${ICON_BASE}/ic_high_school_student.svg`,
    grades: [1, 2, 3],
  },
  {
    id: 'middle_school',
    label: '중학생',
    iconSrc: `${ICON_BASE}/ic_middle_school_student.svg`,
    grades: [1, 2, 3],
  },
  {
    id: 'elementary',
    label: '초등학생',
    iconSrc: `${ICON_BASE}/ic_elementary_school_student.svg`,
    grades: [1, 2, 3, 4, 5, 6],
  },
]

const LEARNER_GROUP_OPTION_ROWS = LEARNER_GROUP_OPTIONS.reduce<LearnerGroupOption[][]>(
  (rows, option, index) => {
    if (index % 2 === 0) {
      rows.push([option])
    } else {
      rows[rows.length - 1].push(option)
    }
    return rows
  },
  [],
)

function LearnerGroupCard({
  option,
  isSelected,
  onSelect,
}: {
  option: LearnerGroupOption
  isSelected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type='button'
      onClick={onSelect}
      aria-pressed={isSelected}
      className={classNames(
        'relative flex min-h-[106px] flex-col items-center justify-center gap-[8px] rounded-[12px] border bg-white p-[12px] text-center',
        isSelected
          ? 'border-button-primary-blue shadow-[0_4px_0_0_#676ED3]'
          : 'border-common-line',
      )}
    >
      <img src={option.iconSrc} alt='' aria-hidden='true' className='h-[40px] w-[40px]' />
      <div className='flex shrink-0 flex-col items-center gap-[2px]'>
        <span
          className={classNames(
            'text-[13px]',
            isSelected ? 'font-medium text-text-blue-primary' : 'text-text-black-primary',
          )}
        >
          {option.label}
        </span>
        {option.description && (
          <span
            className={classNames(
              'text-[11px]',
              isSelected ? 'text-text-blue-primary' : 'text-text-black-secondary',
            )}
          >
            {option.description}
          </span>
        )}
      </div>

      {isSelected && (
        <span className='absolute -top-[4px] right-[8px] flex h-[24px] min-w-[24px] items-center justify-center rounded-full bg-button-primary-blue px-[8px] text-[12px] text-white'>
          ✓
        </span>
      )}
    </button>
  )
}

function GradePanel({
  grades,
  selectedGrade,
  onSelectGrade,
}: {
  grades: number[]
  selectedGrade: number | null
  onSelectGrade: (grade: number) => void
}) {
  return (
    <div className='max-h-[86px] w-full overflow-y-auto rounded-[12px] border border-dashed border-button-secondary-blue bg-white'>
      <div className='flex flex-wrap gap-[8px] p-[8px]'>
        {grades.map((grade) => {
          const isGradeSelected = selectedGrade === grade

          return (
            <button
              key={grade}
              type='button'
              aria-pressed={isGradeSelected}
              onClick={() => {
                onSelectGrade(grade)
              }}
              className={classNames(
                'flex h-[32px] items-center gap-[4px] rounded-full border px-[12px] text-[13px]',
                isGradeSelected
                  ? 'border-button-primary-blue bg-white text-text-blue-primary'
                  : 'border-common-line bg-white text-text-black-secondary',
              )}
            >
              {isGradeSelected && (
                <span
                  aria-hidden='true'
                  className='flex h-[16px] w-[16px] items-center justify-center rounded-full bg-button-primary-blue text-[10px] text-white'
                >
                  ✕
                </span>
              )}
              {grade}학년
            </button>
          )
        })}
      </div>
    </div>
  )
}

// V2 그룹 선택 step (마지막 스텝): 초·중·고는 학년까지 선택해야 제출 가능
export default function LearnerGroupSelect() {
  const {
    selectedUserGroup,
    selectedUserGroupGrade,
    setSelectedUserGroup,
    surveyOnModal,
  } = useSurvey()

  const selectedOption = LEARNER_GROUP_OPTIONS.find((option) => option.id === selectedUserGroup)
  const needsGrade = selectedOption?.grades !== undefined
  const isComplete =
    selectedUserGroup !== null && (!needsGrade || selectedUserGroupGrade !== null)

  return (
    <FormContent>
      <FormContent.Grow className='mb-10'>
        {!surveyOnModal && (
          <FormContent.Question
            title='마지막 단계예요!'
            description='아래에서 나에게 맞는 그룹을 선택해주세요. 더 잘 맞는 학습 콘텐츠와 경험을 준비할게요.'
          />
        )}

        <div
          className={classNames(
            'flex flex-col gap-[8px]',
            surveyOnModal ? 'mt-[10px]' : 'mt-[30px]',
          )}
        >
          {LEARNER_GROUP_OPTION_ROWS.map((row) => {
            const selectedGradeOption = row.find(
              (option) => option.id === selectedUserGroup && option.grades !== undefined,
            )

            return (
              <div key={row.map((option) => option.id).join('-')} className='flex flex-col gap-[4px]'>
                <div className='grid grid-cols-2 gap-[8px]'>
                  {row.map((option) => (
                    <LearnerGroupCard
                      key={option.id}
                      option={option}
                      isSelected={selectedUserGroup === option.id}
                      onSelect={() => {
                        // 그룹을 바꾸면 학년 선택은 초기화
                        setSelectedUserGroup(option.id, null)
                      }}
                    />
                  ))}
                </div>

                {selectedGradeOption?.grades && (
                  <GradePanel
                    grades={selectedGradeOption.grades}
                    selectedGrade={selectedUserGroupGrade}
                    onSelectGrade={(grade) => {
                      setSelectedUserGroup(
                        selectedGradeOption.id,
                        selectedUserGroupGrade === grade ? null : grade,
                      )
                    }}
                  />
                )}
              </div>
            )
          })}
        </div>
      </FormContent.Grow>
      <FormContent.ProcessButton disabled={!isComplete} onNextClick={() => {}} />
    </FormContent>
  )
}
